import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, gql } from '@apollo/client';
import axios from 'axios';
import { BACKEND_URL } from '../config';
import HistorialAcusadoModal from './HistorialAcusadoModal';
import DocumentosFormularios from './DocumentosFormularios';
import { formatDate, formatDateTime } from '../utils/formatDate';

const ESTADOS = [
  { value: 'en_investigacion', label: 'En investigación' },
  { value: 'en_juicio', label: 'En juicio' },
  { value: 'sentenciado', label: 'Sentenciado' },
  { value: 'absuelto', label: 'Absuelto' },
];

const GET_TESTIMONIOS = gql`
  query GetTestimonios($id: uuid!) {
    testimonios(where: { acusado_id: { _eq: $id } }, order_by: { fecha: asc }) {
      id
      testigo
      cedula
      texto
      audio_url
      fecha
    }
  }
`;

const GET_AUDIOS = gql`
  query GetAudios($id: uuid!) {
    audios(where: { acusado_id: { _eq: $id } }, order_by: { fecha: asc }) {
      id
      nombre
      url
      transcripcion
      fecha
    }
  }
`;

const GET_DOCUMENTOS = gql`
  query GetDocumentos($id: uuid!) {
    documentos(where: { acusado_id: { _eq: $id } }, order_by: { fecha: desc }) {
      id
      nombre
      contenido
      fecha
    }
  }
`;

const INSERT_TESTIMONIO = gql`
  mutation InsertTestimonio($object: testimonios_insert_input!) {
    insert_testimonios_one(object: $object) {
      id
      testigo
      cedula
      texto
      audio_url
      fecha
    }
  }
`;

const INSERT_AUDIO = gql`
  mutation InsertAudio($object: audios_insert_input!) {
    insert_audios_one(object: $object) {
      id
      nombre
      url
      transcripcion
      fecha
    }
  }
`;

const UPDATE_AUDIO = gql`
  mutation UpdateAudio($id: uuid!, $transcripcion: String) {
    update_audios_by_pk(pk_columns: { id: $id }, _set: { transcripcion: $transcripcion }) {
      id
      transcripcion
    }
  }
`;

const DELETE_AUDIO = gql`
  mutation DeleteAudio($id: uuid!) {
    delete_audios_by_pk(id: $id) {
      id
    }
  }
`;

const UPDATE_TESTIMONIO = gql`
  mutation UpdateTestimonio($id: uuid!, $_set: testimonios_set_input!) {
    update_testimonios_by_pk(pk_columns: { id: $id }, _set: $_set) {
      id
      texto
      audio_url
    }
  }
`;

const DELETE_TESTIMONIO = gql`
  mutation DeleteTestimonio($id: uuid!) {
    delete_testimonios_by_pk(id: $id) {
      id
    }
  }
`;

const INSERT_DOCUMENTO = gql`
  mutation InsertDocumento($object: documentos_insert_input!) {
    insert_documentos_one(object: $object) {
      id
      nombre
      contenido
      fecha
    }
  }
`;

const UPDATE_ACUSADO = gql`
  mutation UpdateAcusado($id: uuid!, $_set: acusados_set_input!) {
    update_acusados_by_pk(pk_columns: { id: $id }, _set: $_set) {
      id
      estado_actual
      foto
      fecha_nacimiento
      edad
      updated_at
    }
  }
`;

const INSERT_HISTORIAL = gql`
  mutation InsertHistorial($object: historial_acusados_insert_input!) {
    insert_historial_acusados_one(object: $object) {
      id
      detalle_cambio
      fecha_cambio
    }
  }
`;

const mergeAcusadoUpdate = (base, row) => {
  if (!row) return base;
  return {
    ...base,
    estado_procesal: row.estado_actual ?? base.estado_procesal,
    foto: row.foto ?? base.foto,
    fecha_nacimiento: row.fecha_nacimiento ?? base.fecha_nacimiento,
    edad: row.edad ?? base.edad,
    fecha_actualizacion: row.updated_at
      ? formatDate(row.updated_at)
      : base.fecha_actualizacion,
  };
};

const transcribirAudio = async (fileUrl) => {
  if (!fileUrl || !BACKEND_URL) return '';
  const cleanUrl = BACKEND_URL.trim().replace(/\/$/, '');
  const res = await axios.post(`${cleanUrl}/api/transcribe`, { fileUrl });
  return res.data?.transcription || '';
};

const subirAudio = async (blob) => {
  if (!blob || !BACKEND_URL) return '';
  const cleanUrl = BACKEND_URL.trim().replace(/\/$/, '');
  const formData = new FormData();
  const filename = `audio_${Date.now()}.webm`;
  const file = new File([blob], filename, { type: blob.type || 'audio/webm' });
  formData.append('file', file);
  const res = await axios.post(`${cleanUrl}/api/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const url = res.data?.url || '';
  if (!url) return '';
  return url.startsWith('http') ? url : `${cleanUrl}${url}`;
};

export default function DetalleAcusadoPage({ acusado, onVolver, onActualizarAcusado }) {
  const [estado, setEstado] = useState(acusado.estado_procesal);
  const [fotoPreview, setFotoPreview] = useState(acusado.foto || '');
  const [fotoErr, setFotoErr] = useState('');
  const [fechaNacimiento, setFechaNacimiento] = useState(acusado.fecha_nacimiento || '');
  const [edad, setEdad] = useState(acusado.edad || '');
  const [testimonios, setTestimonios] = useState([]);
  const [nombreTestigo, setNombreTestigo] = useState('');
  const [apellidoTestigo, setApellidoTestigo] = useState('');
  const [cedulaTestigo, setCedulaTestigo] = useState('');
  const [nuevoTestimonio, setNuevoTestimonio] = useState('');
  const [grabando, setGrabando] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [chunks, setChunks] = useState([]);
  const [audioPreview, setAudioPreview] = useState('');
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [audioSubiendo, setAudioSubiendo] = useState(false);
  const [audioTranscripcion, setAudioTranscripcion] = useState('');
  const [documentos, setDocumentos] = useState([]);
  const [audios, setAudios] = useState([]);
  const [verHistorial, setVerHistorial] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [msg, setMsg] = useState('');
  const [transcribiendoId, setTranscribiendoId] = useState('');
  // Transcripción manual
  const [manualOpen, setManualOpen] = useState(false);
  const [manualFuente, setManualFuente] = useState(null); // 'audio' | 'testimonio'
  const [manualAudioItem, setManualAudioItem] = useState(null);
  const [manualTestimonioItem, setManualTestimonioItem] = useState(null);
  const [manualAudioUrl, setManualAudioUrl] = useState('');
  const [manualTexto, setManualTexto] = useState('');
  const audioRef = useRef(null);

  const { data: dataTestimonios } = useQuery(GET_TESTIMONIOS, {
    variables: { id: acusado.acusado_id },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  const { data: dataAudios } = useQuery(GET_AUDIOS, {
    variables: { id: acusado.acusado_id },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  const { data: dataDocumentos, refetch: refetchDocumentos } = useQuery(GET_DOCUMENTOS, {
    variables: { id: acusado.acusado_id },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const [insertTestimonio] = useMutation(INSERT_TESTIMONIO);
  const [insertAudio] = useMutation(INSERT_AUDIO);
  const [updateAudio] = useMutation(UPDATE_AUDIO);
  const [deleteAudio] = useMutation(DELETE_AUDIO);
  const [updateTestimonio] = useMutation(UPDATE_TESTIMONIO);
  const [deleteTestimonio] = useMutation(DELETE_TESTIMONIO);
  const [editTestimonioId, setEditTestimonioId] = useState('');
  const [editTestimonioTexto, setEditTestimonioTexto] = useState('');
  const [insertDocumento] = useMutation(INSERT_DOCUMENTO);
  const [updateAcusado] = useMutation(UPDATE_ACUSADO);
  const [insertHistorial] = useMutation(INSERT_HISTORIAL);

  useEffect(() => {
    if (dataTestimonios?.testimonios) {
      setTestimonios(dataTestimonios.testimonios);
    }
  }, [dataTestimonios]);

  useEffect(() => {
    if (dataAudios?.audios) {
      setAudios(dataAudios.audios);
    }
  }, [dataAudios]);

  useEffect(() => {
    if (dataDocumentos?.documentos) {
      setDocumentos(dataDocumentos.documentos);
    }
  }, [dataDocumentos]);

  // Reemplazo de variables en plantilla
  const renderDocumento = (doc) => {
    return doc.plantilla
      .replace('{{abogado}}', 'Dr. Legal')
      .replace('{{acusado}}', acusado.nombre)
      .replace('{{caso}}', 'Expediente 1234');
  };

  const handleFotoChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const validTypes = ['image/jpeg','image/png','image/webp'];
    if (!validTypes.includes(file.type)) { setFotoErr('Formato no soportado'); return; }
    if (file.size > 2*1024*1024) { setFotoErr('Imagen > 2MB'); return; }
    setFotoErr('');
    const reader = new FileReader();
    reader.onload = () => setFotoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const guardarFoto = async () => {
    try {
      const res = await updateAcusado({ variables: { id: acusado.acusado_id, _set: { foto: fotoPreview } } });
      const actualizado = mergeAcusadoUpdate(acusado, res.data?.update_acusados_by_pk);
      onActualizarAcusado && onActualizarAcusado(actualizado);
      await insertHistorial({
        variables: {
          object: {
            acusado_id: acusado.acusado_id,
            detalle_cambio: 'Foto actualizada',
          },
        },
      });
      setMsg('Foto actualizada');
    } catch (err) {
      setMsg('No se pudo guardar la foto');
    } finally {
      setTimeout(() => setMsg(''), 1500);
    }
  };

  const handleAgregarTestimonio = async (e) => {
    e.preventDefault();
    if (!nombreTestigo || !apellidoTestigo || !cedulaTestigo) return;
    if (!nuevoTestimonio && !audioBlob && !audioUrl) return;

    const nombreCompleto = `${nombreTestigo} ${apellidoTestigo}`.trim();
    let finalAudioUrl = audioUrl;
    let audioId = null;

    try {
      if (audioBlob && !finalAudioUrl) {
        setAudioSubiendo(true);
        finalAudioUrl = await subirAudio(audioBlob);
        setAudioUrl(finalAudioUrl);
        setAudioSubiendo(false);
      }

      if (finalAudioUrl) {
        const nombre = `testimonio_${Date.now()}.webm`;
        const audioRes = await insertAudio({
          variables: {
            object: {
              acusado_id: acusado.acusado_id,
              nombre,
              url: finalAudioUrl,
              transcripcion: audioTranscripcion || nuevoTestimonio || null,
            },
          },
        });
        if (audioRes.data?.insert_audios_one) {
          audioId = audioRes.data.insert_audios_one.id;
          setAudios(prev => [...prev, audioRes.data.insert_audios_one]);
        }
      }

      const res = await insertTestimonio({
        variables: {
          object: {
            acusado_id: acusado.acusado_id,
            testigo: nombreCompleto,
            cedula: cedulaTestigo,
            texto: nuevoTestimonio || audioTranscripcion || '(transcripción pendiente)',
            audio_url: finalAudioUrl || null,
          },
        },
      });
      if (res.data?.insert_testimonios_one) {
        setTestimonios(prev => [...prev, res.data.insert_testimonios_one]);
      }

      await insertHistorial({
        variables: {
          object: {
            acusado_id: acusado.acusado_id,
            detalle_cambio: `Testimonio de ${nombreCompleto} (${cedulaTestigo})`,
          },
        },
      });

      if (audioId && audioTranscripcion && !nuevoTestimonio) {
        setMsg('Audio registrado con transcripción');
      }
    } catch (err) {
      setMsg('No se pudo guardar el testimonio');
      setTimeout(() => setMsg(''), 1500);
    } finally {
      setNombreTestigo('');
      setApellidoTestigo('');
      setCedulaTestigo('');
      setNuevoTestimonio('');
      setAudioPreview('');
      setAudioBlob(null);
      setAudioUrl('');
      setAudioTranscripcion('');
      setAudioSubiendo(false);
    }
  };

  const handleGuardarEstado = async () => {
    setGuardando(true);
    try {
      const res = await updateAcusado({
        variables: { id: acusado.acusado_id, _set: { estado_actual: estado } },
      });
      const actualizado = mergeAcusadoUpdate(acusado, res.data?.update_acusados_by_pk);
      onActualizarAcusado && onActualizarAcusado(actualizado);
      await insertHistorial({
        variables: {
          object: {
            acusado_id: acusado.acusado_id,
            estado_anterior: acusado.estado_procesal,
            estado_nuevo: estado,
            detalle_cambio: `Estado actualizado a ${estado}`,
          },
        },
      });
      setMsg('Estado actualizado');
    } finally {
      setGuardando(false);
      setTimeout(() => setMsg(''), 1500);
    }
  };

  const handleAutoTranscribirActual = async () => {
    if (!audioBlob && !audioUrl) return;
    try {
      setTranscribiendoId('nuevo');
      let finalAudioUrl = audioUrl;
      if (!finalAudioUrl && audioBlob) {
        setAudioSubiendo(true);
        finalAudioUrl = await subirAudio(audioBlob);
        setAudioUrl(finalAudioUrl);
        setAudioSubiendo(false);
      }
      const texto = await transcribirAudio(finalAudioUrl);
      if (!texto) throw new Error('Transcripcion vacia');
      setNuevoTestimonio(texto);
      setAudioTranscripcion(texto);
      setMsg('Transcripción lista, revise el texto antes de guardar');
      setTimeout(() => setMsg(''), 1500);
    } catch (err) {
      setMsg('No se pudo transcribir el audio');
      setTimeout(() => setMsg(''), 2000);
    } finally {
      setTranscribiendoId('');
      setAudioSubiendo(false);
    }
  };

  // Grabación desde micrófono para testimonio
  const iniciarGrabacion = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const localChunks = [];
      rec.ondataavailable = (ev) => { if (ev.data.size > 0) localChunks.push(ev.data); };
      rec.onstop = () => {
        const blob = new Blob(localChunks, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setAudioPreview(url);
        setAudioBlob(blob);
        setChunks([]);
      };
      setChunks(localChunks);
      setMediaRecorder(rec);
      rec.start();
      setGrabando(true);
    } catch (err) {
      setMsg('No se pudo acceder al micrófono.');
      setTimeout(() => setMsg(''), 2000);
    }
  };

  const detenerGrabacion = () => {
    if (mediaRecorder && grabando) {
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach(t => t.stop());
      setGrabando(false);
    }
  };

  const handleDocumentoCreado = async () => {
    setMsg('Documento generado');
    setTimeout(() => setMsg(''), 1500);
    if (typeof refetchDocumentos === 'function') {
      const res = await refetchDocumentos();
      if (res.data?.documentos) setDocumentos(res.data.documentos);
    }
  };

  // Transcribir un audio y actualizar el testimonio asociado
  const handleTranscribir = async ({ fuente, audioItem, testimonioItem }) => {
    try {
      const key = fuente === 'audio' ? `a-${audioItem.id}` : `t-${testimonioItem.id}`;
      const url = fuente === 'audio' ? audioItem.url : testimonioItem.audio_url;
      if (!url) return;

      // Si ya existe transcripción, confirmar y reutilizar en lugar de reprocesar
      if (fuente === 'audio' && audioItem && audioItem.transcripcion) {
        const ok = window.confirm('Este audio ya tiene transcripción. ¿Desea reemplazar el testimonio con esa transcripción?');
        if (!ok) return;
      }
      if (fuente === 'testimonio') {
        const match = audios.find(a => a.url && a.url === testimonioItem.audio_url);
        if (match && match.transcripcion) {
          const ok = window.confirm('Ya existe transcripción para este audio. ¿Desea reemplazar el testimonio con esa transcripción?');
          if (!ok) return;
        }
      }

      const testimonioTarget = fuente === 'testimonio'
        ? testimonioItem
        : testimonios.find(t => t.audio_url && audioItem && t.audio_url === audioItem.url);

      // Transcribir o reutilizar transcripción
      setTranscribiendoId(key);
      const texto = (audioItem && audioItem.transcripcion) ? audioItem.transcripcion : await transcribirAudio(url);
      if (!texto) throw new Error('Transcripcion vacia');

      // Actualizar transcripción en el recurso de audio si aplica
      if (fuente === 'audio' && audioItem && audioItem.id) {
        if (!audioItem.transcripcion) {
          const res = await updateAudio({ variables: { id: audioItem.id, transcripcion: texto } });
          if (res.data?.update_audios_by_pk) {
            setAudios(prev => prev.map(a => a.id === res.data.update_audios_by_pk.id ? { ...a, transcripcion: res.data.update_audios_by_pk.transcripcion } : a));
          }
        }
      } else if (fuente === 'testimonio') {
        // Si el testimonio corresponde a un audio en la lista, actualizarlo
        const match = audios.find(a => a.url && a.url === testimonioItem.audio_url);
        if (match) {
          const res = await updateAudio({ variables: { id: match.id, transcripcion: texto } });
          if (res.data?.update_audios_by_pk) {
            setAudios(prev => prev.map(a => a.id === res.data.update_audios_by_pk.id ? { ...a, transcripcion: res.data.update_audios_by_pk.transcripcion } : a));
          }
        }
      }

      if (testimonioTarget?.id) {
        const res = await updateTestimonio({ variables: { id: testimonioTarget.id, _set: { texto } } });
        if (res.data?.update_testimonios_by_pk) {
          setTestimonios(prev => prev.map(t => t.id === res.data.update_testimonios_by_pk.id ? { ...t, texto: res.data.update_testimonios_by_pk.texto } : t));
        }
      } else if (fuente === 'audio' && audioItem) {
        const res = await insertTestimonio({
          variables: {
            object: {
              acusado_id: acusado.acusado_id,
              testigo: 'Transcripción de audio',
              texto,
              cedula: '',
              audio_url: audioItem.url,
            },
          },
        });
        if (res.data?.insert_testimonios_one) {
          setTestimonios(prev => [...prev, res.data.insert_testimonios_one]);
        }
      }

      setMsg('Transcripción aplicada al testimonio');
      setTimeout(() => setMsg(''), 1500);
    } catch (err) {
      setMsg('No se pudo transcribir el audio');
      setTimeout(() => setMsg(''), 2000);
    } finally {
      setTranscribiendoId('');
    }
  };

  const calcularEdad = (fechaStr) => {
    if (!fechaStr) return '';
    const hoy = new Date();
    const fn = new Date(fechaStr);
    if (isNaN(fn.getTime())) return '';
    let val = hoy.getFullYear() - fn.getFullYear();
    const m = hoy.getMonth() - fn.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < fn.getDate())) val--;
    return Math.max(0, val);
  };

  const guardarDatosPersonales = async () => {
    try {
      const res = await updateAcusado({
        variables: { id: acusado.acusado_id, _set: { fecha_nacimiento: fechaNacimiento, edad } },
      });
      const actualizado = mergeAcusadoUpdate(acusado, res.data?.update_acusados_by_pk);
      onActualizarAcusado && onActualizarAcusado(actualizado);
      await insertHistorial({
        variables: {
          object: {
            acusado_id: acusado.acusado_id,
            detalle_cambio: 'Datos personales actualizados',
          },
        },
      });
      setMsg('Datos personales actualizados');
    } finally {
      setTimeout(() => setMsg(''), 1500);
    }
  };

  // ----- Transcripción manual -----
  const abrirTranscripcionManualDesdeAudio = (a) => {
    setManualFuente('audio');
    setManualAudioItem(a);
    setManualTestimonioItem(null);
    setManualAudioUrl(a.url || '');
    setManualTexto(a.transcripcion || '');
    setManualOpen(true);
  };

  const abrirTranscripcionManualDesdeTestimonio = (t) => {
    setManualFuente('testimonio');
    setManualAudioItem(null);
    setManualTestimonioItem(t);
    setManualAudioUrl(t.audio_url || '');
    // Si existe audio emparejado, precargar su transcripción si la hay
    const match = audios.find(x => x.url && x.url === t.audio_url);
    setManualTexto((match && match.transcripcion) ? match.transcripcion : '');
    setManualOpen(true);
  };

  const cerrarManual = () => {
    setManualOpen(false);
    setManualFuente(null);
    setManualAudioItem(null);
    setManualTestimonioItem(null);
    setManualAudioUrl('');
    setManualTexto('');
  };

  const manualSkip = (delta) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime + delta);
    }
  };

  const manualVel = (factor) => {
    if (audioRef.current) {
      audioRef.current.playbackRate = Math.max(0.25, Math.min(2, (audioRef.current.playbackRate || 1) * factor));
    }
  };

  const guardarTranscripcionManual = async () => {
    try {
      // Si hay audio concreto o emparejado, actualizar su transcripción
      if (manualFuente === 'audio' && manualAudioItem) {
        const res = await updateAudio({ variables: { id: manualAudioItem.id, transcripcion: manualTexto } });
        if (res.data?.update_audios_by_pk) {
          setAudios(prev => prev.map(a => a.id === res.data.update_audios_by_pk.id ? { ...a, transcripcion: res.data.update_audios_by_pk.transcripcion } : a));
        }
      }
      if (manualFuente === 'testimonio' && manualTestimonioItem) {
        const match = audios.find(a => a.url && a.url === manualTestimonioItem.audio_url);
        if (match) {
          const res = await updateAudio({ variables: { id: match.id, transcripcion: manualTexto } });
          if (res.data?.update_audios_by_pk) {
            setAudios(prev => prev.map(a => a.id === res.data.update_audios_by_pk.id ? { ...a, transcripcion: res.data.update_audios_by_pk.transcripcion } : a));
          }
        }
      }
      if (manualFuente === 'testimonio' && manualTestimonioItem?.id) {
        const res = await updateTestimonio({ variables: { id: manualTestimonioItem.id, _set: { texto: manualTexto } } });
        if (res.data?.update_testimonios_by_pk) {
          setTestimonios(prev => prev.map(t => t.id === res.data.update_testimonios_by_pk.id ? { ...t, texto: res.data.update_testimonios_by_pk.texto } : t));
        }
      } else if (manualAudioUrl) {
        const res = await insertTestimonio({
          variables: {
            object: {
              acusado_id: acusado.acusado_id,
              testigo: 'Transcripción manual',
              texto: manualTexto,
              cedula: '',
              audio_url: manualAudioUrl,
            },
          },
        });
        if (res.data?.insert_testimonios_one) {
          setTestimonios(prev => [...prev, res.data.insert_testimonios_one]);
        }
      }
      setMsg('Transcripción manual guardada');
      setTimeout(() => setMsg(''), 1500);
      cerrarManual();
    } catch (e) {
      setMsg('No se pudo guardar la transcripción manual');
      setTimeout(() => setMsg(''), 2000);
    }
  };

  const handleEditarTestimonio = (t) => {
    setEditTestimonioId(t.id);
    setEditTestimonioTexto(t.texto || '');
  };

  const handleCancelarEdicion = () => {
    setEditTestimonioId('');
    setEditTestimonioTexto('');
  };

  const handleGuardarEdicion = async () => {
    if (!editTestimonioId) return;
    try {
      const res = await updateTestimonio({ variables: { id: editTestimonioId, _set: { texto: editTestimonioTexto } } });
      if (res.data?.update_testimonios_by_pk) {
        setTestimonios(prev => prev.map(t => t.id === editTestimonioId ? { ...t, texto: editTestimonioTexto } : t));
      }
      setMsg('Testimonio actualizado');
      setTimeout(() => setMsg(''), 1500);
      handleCancelarEdicion();
    } catch (err) {
      setMsg('No se pudo actualizar el testimonio');
      setTimeout(() => setMsg(''), 2000);
    }
  };

  const handleEliminarTestimonio = async (t) => {
    const ok = window.confirm('¿Seguro que desea eliminar este testimonio?');
    if (!ok) return;
    try {
      const res = await deleteTestimonio({ variables: { id: t.id } });
      if (res.data?.delete_testimonios_by_pk) {
        setTestimonios(prev => prev.filter(x => x.id !== t.id));
      }
      setMsg('Testimonio eliminado');
      setTimeout(() => setMsg(''), 1500);
    } catch (err) {
      setMsg('No se pudo eliminar el testimonio');
      setTimeout(() => setMsg(''), 2000);
    }
  };

  const handleEliminarAudio = async (a) => {
    const ok = window.confirm('¿Seguro que desea eliminar este audio?');
    if (!ok) return;
    try {
      const res = await deleteAudio({ variables: { id: a.id } });
      if (res.data?.delete_audios_by_pk) {
        setAudios(prev => prev.filter(x => x.id !== a.id));
      }
      setMsg('Audio eliminado');
      setTimeout(() => setMsg(''), 1500);
    } catch (err) {
      setMsg('No se pudo eliminar el audio');
      setTimeout(() => setMsg(''), 2000);
    }
  };

  return (
    <div className="container py-4">
      <button className="btn btn-outline-primary btn-volver mb-3" onClick={onVolver}>
        <i className="bi bi-arrow-left"></i>
        <span>Volver</span>
      </button>
      <div className="d-flex flex-column gap-4">
        {/* Datos generales y cambio de estatus */}
        <div>
          <div className="card mb-3">
            <div className="card-header">Datos del Acusado</div>
            <div className="card-body">
              <div className="mb-3 text-center">
                <img src={fotoPreview || acusado.foto || 'https://via.placeholder.com/140?text=Sin+foto'} alt={acusado.nombre}
                     style={{ width: 140, height: 140, objectFit: 'cover', borderRadius: '50%', border: '2px solid #eee' }} />
                <div className="d-flex justify-content-center gap-2 mt-2">
                  <label className="btn btn-sm btn-outline-secondary mb-0">
                    Cambiar foto <input type="file" accept="image/*" className="d-none" onChange={handleFotoChange} />
                  </label>
                  <button className="btn btn-sm btn-primary" type="button" onClick={guardarFoto} disabled={!fotoPreview}>Guardar</button>
                </div>
                {fotoErr && <div className="text-danger small mt-1">{fotoErr}</div>}
              </div>
              <div><b>Nombre:</b> {acusado.nombre}</div>
              <div><b>Grado:</b> {acusado.grado}</div>
              <div><b>Unidad:</b> {acusado.unidad}</div>
              <div className="row g-2 mt-2">
                <div className="col-12 col-md-6">
                  <label className="form-label">Fecha de nacimiento</label>
                  <input className="form-control" type="date" value={fechaNacimiento} onChange={(e) => { const v = e.target.value; setFechaNacimiento(v); setEdad(calcularEdad(v)); }} />
                </div>
                <div className="col-6 col-md-3">
                  <label className="form-label">Edad</label>
                  <input className="form-control" type="number" min="0" value={edad} readOnly />
                </div>
                <div className="col-6 col-md-3 d-flex align-items-end">
                  <button className="btn btn-outline-primary w-100" type="button" onClick={guardarDatosPersonales} disabled={!fechaNacimiento}>Guardar datos</button>
                </div>
              </div>
              <div className="mt-2"><b>Estado procesal:</b></div>
              <select className="form-select mt-1 mb-2" value={estado} onChange={e => setEstado(e.target.value)}>
                {ESTADOS.map(eo => <option key={eo.value} value={eo.value}>{eo.label}</option>)}
              </select>
              <div className="d-flex justify-content-between align-items-center">
                <div><b>Fecha actualización:</b> {formatDate(acusado.fecha_actualizacion)}</div>
                <button className="btn btn-outline-secondary btn-sm" onClick={() => setVerHistorial(true)}>Ver historial</button>
              </div>
              {msg && <div className="alert alert-success py-1 mt-2">{msg}</div>}
              <button className="btn btn-primary mt-2" onClick={handleGuardarEstado} disabled={guardando}>Guardar estado</button>
            </div>
          </div>
        </div>
        {/* Testimonios y testigos */}
        <div>
          <div className="card mb-3">
            <div className="card-header">Testimonios y testigos</div>
            <div className="card-body">
              <div className="mb-2">
                <div className="form-text">Agrega testigos y su testimonio. Puedes registrar varios.</div>
              </div>
              <form onSubmit={handleAgregarTestimonio} className="row g-2 align-items-start">
                <div className="col-12 col-md-4">
                  <label className="form-label">Nombre</label>
                  <input className="form-control" placeholder="Nombre" value={nombreTestigo} onChange={e => setNombreTestigo(e.target.value)} />
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label">Apellido</label>
                  <input className="form-control" placeholder="Apellido" value={apellidoTestigo} onChange={e => setApellidoTestigo(e.target.value)} />
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label">Cédula</label>
                  <input className="form-control" placeholder="Cédula" value={cedulaTestigo} onChange={e => setCedulaTestigo(e.target.value)} />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">Testimonio</label>
                  <textarea className="form-control" rows={3} placeholder="Describe el testimonio" value={nuevoTestimonio} onChange={e => setNuevoTestimonio(e.target.value)} />
                </div>
                <div className="col-12 col-md-2 d-grid">
                  <label className="form-label">Audio</label>
                  {!grabando ? (
                    <button type="button" className="btn btn-outline-primary" onClick={iniciarGrabacion}>
                      <i className="bi bi-mic"></i> Grabar
                    </button>
                  ) : (
                    <button type="button" className="btn btn-danger" onClick={detenerGrabacion}>
                      <i className="bi bi-stop-circle"></i> Detener
                    </button>
                  )}
                </div>
                <div className="col-12 col-md-2 d-grid">
                  <label className="form-label opacity-0">.</label>
                  <button
                    className="btn btn-success"
                    type="submit"
                    disabled={!nombreTestigo || !apellidoTestigo || !cedulaTestigo || (!nuevoTestimonio && !audioBlob && !audioUrl) || audioSubiendo}
                  >
                    {audioSubiendo ? 'Subiendo...' : 'Guardar'}
                  </button>
                </div>
                <div className="col-12 col-md-2 d-grid">
                  <label className="form-label opacity-0">.</label>
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={handleAutoTranscribirActual}
                    disabled={!audioBlob && !audioUrl}
                  >
                    {transcribiendoId === 'nuevo' ? (
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    ) : (
                      <i className="bi bi-translate me-1"></i>
                    )}
                    Transcribir
                  </button>
                </div>
              </form>
              {audioPreview && (
                <div className="mt-2">
                  <audio controls src={audioPreview}></audio>
                  <button className="btn btn-sm btn-outline-secondary ms-2" onClick={() => { setAudioPreview(''); setAudioBlob(null); setAudioUrl(''); setAudioTranscripcion(''); }}>Quitar audio</button>
                </div>
              )}
              <ul className="list-group mt-3">
                {testimonios.length === 0 && (<li className="list-group-item text-muted">Sin testimonios aún.</li>)}
                {testimonios.map(t => (
                  <li className="list-group-item" key={t.id}>
                    <div className="fw-bold">{t.testigo}</div>
                    {t.cedula && <div className="small text-muted">Cédula: {t.cedula}</div>}
                    <div className="small">{formatDateTime(t.fecha)}</div>
                    {editTestimonioId === t.id ? (
                      <div className="mt-2">
                        <textarea className="form-control" rows={3} value={editTestimonioTexto} onChange={e => setEditTestimonioTexto(e.target.value)} />
                        <div className="d-flex gap-2 mt-2">
                          <button className="btn btn-sm btn-primary" onClick={handleGuardarEdicion}>Guardar</button>
                          <button className="btn btn-sm btn-outline-secondary" onClick={handleCancelarEdicion}>Cancelar</button>
                        </div>
                      </div>
                    ) : (
                      <div>{t.texto}</div>
                    )}
                    {t.audio_url && (
                      <div className="mt-2 d-flex align-items-center gap-2">
                        <audio controls src={t.audio_url}></audio>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          disabled={!!transcribiendoId}
                          onClick={() => handleTranscribir({ fuente: 'testimonio', testimonioItem: t })}
                        >
                          {transcribiendoId === `t-${t.id}` ? (
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          ) : (
                            <i className="bi bi-translate me-1"></i>
                          )}
                          Transcribir
                        </button>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => abrirTranscripcionManualDesdeTestimonio(t)}
                        >
                          <i className="bi bi-pencil-square me-1"></i>
                          Transcribir manualmente
                        </button>
                      </div>
                    )}
                    <div className="d-flex gap-2 mt-2">
                      <button className="btn btn-sm btn-outline-primary" onClick={() => handleEditarTestimonio(t)}>
                        <i className="bi bi-pencil me-1"></i>
                        Editar
                      </button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => handleEliminarTestimonio(t)}>
                        <i className="bi bi-trash me-1"></i>
                        Eliminar
                      </button>
                    </div>
                    {t.audio_url && audios.find(a => a.url === t.audio_url)?.transcripcion && (
                      <div className="mt-2">
                        <div className="small text-muted">Transcripción</div>
                        <div className="border rounded p-2 bg-light small">{audios.find(a => a.url === t.audio_url)?.transcripcion}</div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        {/* Audios subidos */}
        <div>
          <div className="card mb-3">
            <div className="card-header">Audios</div>
            <div className="card-body">
              {audios.length === 0 && <div className="text-muted">Aún no hay audios.</div>}
              <ul className="list-group">
                {audios.map(a => (
                  <li key={a.id} className="list-group-item">
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                      <div>
                        <div className="fw-bold">{a.nombre || 'Audio'}</div>
                        <div className="small text-muted">{formatDateTime(a.fecha)}</div>
                        {testimonios.find(t => t.audio_url === a.url) && (
                          <div className="small">Testimonio: {testimonios.find(t => t.audio_url === a.url)?.testigo}</div>
                        )}
                        <audio controls src={a.url} className="mt-2" />
                        {a.transcripcion && (
                          <div className="mt-2">
                            <div className="small text-muted">Transcripción</div>
                            <div className="border rounded p-2 bg-light small">{a.transcripcion}</div>
                          </div>
                        )}
                      </div>
                      <div className="d-flex align-items-start">
                        <button
                          className="btn btn-outline-primary btn-sm"
                          disabled={!!transcribiendoId}
                          onClick={() => handleTranscribir({ fuente: 'audio', audioItem: a })}
                        >
                          {transcribiendoId === `a-${a.id}` ? (
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          ) : (
                            <i className="bi bi-translate me-1"></i>
                          )}
                          Transcribir
                        </button>
                        <button
                          className="btn btn-outline-secondary btn-sm ms-2"
                          onClick={() => abrirTranscripcionManualDesdeAudio(a)}
                        >
                          <i className="bi bi-pencil-square me-1"></i>
                          Transcribir manualmente
                        </button>
                        <button
                          className="btn btn-outline-danger btn-sm ms-2"
                          onClick={() => handleEliminarAudio(a)}
                        >
                          <i className="bi bi-trash me-1"></i>
                          Eliminar
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        {/* Documentos: formularios y generados */}
        <div>
          <div className="card mb-3">
            <div className="card-header">Formularios de documentos</div>
            <div className="card-body">
              <DocumentosFormularios acusado={acusado} onDocumentoCreado={handleDocumentoCreado} />
            </div>
          </div>
          <div className="card mb-3">
            <div className="card-header">Documentos generados</div>
            <div className="card-body">
              {documentos.length === 0 && <div className="text-muted">Aún no hay documentos.</div>}
              {documentos.map(doc => (
                <div key={doc.id} className="mb-3">
                  <div className="fw-bold">{doc.nombre}</div>
                  <div className="small text-muted">{formatDateTime(doc.fecha)}</div>
                  <pre className="bg-light p-2 small">{doc.contenido}</pre>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {verHistorial && <HistorialAcusadoModal acusado={acusado} onClose={() => setVerHistorial(false)} />}
      {/* Modal de transcripción manual */}
      {manualOpen && (
        <div className="position-fixed top-0 start-0 w-100 h-100" style={{ background: 'rgba(0,0,0,.5)', zIndex: 1050 }}>
          <div className="container h-100 d-flex align-items-center justify-content-center">
            <div className="card shadow" style={{ maxWidth: 900, width: '100%' }}>
              <div className="card-header d-flex justify-content-between align-items-center">
                <div>Transcripción manual</div>
                <button className="btn btn-sm btn-outline-secondary" onClick={cerrarManual}>
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
              <div className="card-body">
                {!manualAudioUrl && (
                  <div className="alert alert-warning py-2">No hay audio disponible para reproducir; puede escribir la transcripción manualmente.</div>
                )}
                {manualAudioUrl && (
                  <div className="mb-3">
                    <audio ref={audioRef} controls src={manualAudioUrl} className="w-100" />
                    <div className="d-flex gap-2 mt-2">
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => manualSkip(-5)}>
                        <i className="bi bi-skip-backward"></i> -5s
                      </button>
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => manualSkip(5)}>
                        +5s <i className="bi bi-skip-forward"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => manualVel(1/1.25)}>
                        <i className="bi bi-dash-circle"></i> Más lento
                      </button>
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => manualVel(1.25)}>
                        <i className="bi bi-plus-circle"></i> Más rápido
                      </button>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={async () => {
                          try {
                            setTranscribiendoId('manual');
                            const texto = await transcribirAudio(manualAudioUrl);
                            if (texto) setManualTexto(texto);
                          } finally {
                            setTranscribiendoId('');
                          }
                        }}
                      >
                        {transcribiendoId === 'manual' ? (
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        ) : (
                          <i className="bi bi-translate me-1"></i>
                        )}
                        Transcribir automático
                      </button>
                    </div>
                  </div>
                )}
                <label className="form-label">Texto de la transcripción</label>
                <textarea className="form-control" rows={8} value={manualTexto} onChange={e => setManualTexto(e.target.value)} placeholder="Escribe aquí la transcripción..."></textarea>
                <div className="d-flex justify-content-end gap-2 mt-3">
                  <button className="btn btn-outline-secondary" onClick={cerrarManual}>Cancelar</button>
                  <button className="btn btn-primary" onClick={guardarTranscripcionManual} disabled={!manualTexto.trim()}>Guardar</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
